import { useEffect, useState } from "react";
import { api, jsonOptions } from "./api";
import { LoginPage } from "./LoginPage";
import { DayPage } from "./DayPage";
import { ActivitiesPage } from "./ActivitiesPage";
import { AnalyticsPage } from "./AnalyticsPage";
import { SettingsPage } from "./SettingsPage";
import { LoadingBlock } from "./components";
import type { Direction, SessionUser } from "../shared/types";

type Page = "today" | "analytics" | "activities" | "settings";
function todayInHongKong() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Hong_Kong", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

const NAV: Array<{ id: Page; label: string; icon: string }> = [
  { id: "today", label: "每日记录", icon: "⌁" },
  { id: "analytics", label: "时间统计", icon: "▥" },
  { id: "activities", label: "事项库", icon: "◎" },
  { id: "settings", label: "设置与备份", icon: "⚙" },
];

export default function App() {
  const [session, setSession] = useState<{ loading: boolean; user: SessionUser | null }>({ loading: true, user: null });
  const [directions, setDirections] = useState<Direction[]>([]);
  const [page, setPage] = useState<Page>(() => (sessionStorage.getItem("timeflow_page") as Page) || "today");
  useEffect(() => {
    api<{ authenticated: boolean; user: SessionUser | null }>("/auth/session")
      .then((result) => setSession({ loading: false, user: result.authenticated ? result.user : null }))
      .catch(() => setSession({ loading: false, user: null }));
    const unauthorized = () => { setSession({ loading: false, user: null }); setDirections([]); };
    window.addEventListener("timeflow:unauthorized", unauthorized);
    return () => window.removeEventListener("timeflow:unauthorized", unauthorized);
  }, []);
  useEffect(() => {
    if (!session.user) return;
    api<Direction[]>("/directions").then(setDirections).catch(() => setDirections([]));
  }, [session.user?.id]);
  async function refreshDirections() { setDirections(await api<Direction[]>("/directions")); }
  function navigate(next: Page) { setPage(next); sessionStorage.setItem("timeflow_page", next); }
  async function logout() { await api("/auth/logout", jsonOptions("POST")); setSession({ loading: false, user: null }); setDirections([]); }
  if (session.loading) return <div className="boot-screen"><div className="brand-symbol large"><span /><span /><span /></div><LoadingBlock label="正在打开 TimeFlow…" /></div>;
  if (!session.user) return <LoginPage onLogin={(user) => setSession({ loading: false, user })} />;
  const today = todayInHongKong();
  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><div className="brand-symbol"><span /><span /><span /></div><div><strong>TimeFlow</strong><small>时间流向记录</small></div></div>
      <nav>{NAV.map((item) => <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => navigate(item.id)}><span>{item.icon}</span>{item.label}</button>)}</nav>
      <div className="sidebar-foot"><div className="user-block"><span>{session.user.username.slice(0, 1).toUpperCase()}</span><div><strong>{session.user.username}</strong><small>{session.user.role === "admin" ? "管理员" : "个人账户"}</small></div></div><button className="logout-button" onClick={logout}>退出登录</button></div>
    </aside>
    <main className="main-content">
      {page === "today" && <DayPage initialDate={today} directions={directions} onDirectionsChanged={refreshDirections} />}
      {page === "analytics" && <AnalyticsPage today={today} />}
      {page === "activities" && <ActivitiesPage directions={directions} onDirectionsChanged={refreshDirections} />}
      {page === "settings" && <SettingsPage user={session.user} onUserChanged={(user) => setSession({ loading: false, user })} onDataChanged={refreshDirections} />}
    </main>
  </div>;
}
